import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { db, storage } from '../firebase';
import { collection, addRef, addDoc, serverTimestamp, runTransaction, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './ReceiptPage.css';

const VAT_RATE = 0.18; // 18% VAT in Israel

const LESSON_TYPES = [
  'שיעור רגיל',
  'שיעור וחצי',
  'שיעור כפול',
  'העמדת רכב לטסט',
  'רישום לבית ספר',
  'טסט פנימי'
];

const ReceiptPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pin, setPin] = useState('');

  // Form State
  const [payerName, setPayerName] = useState('');
  const [items, setItems] = useState([]);
  const [draftItem, setDraftItem] = useState({
    id: Date.now(), lessonType: 'שיעור רגיל', quantity: '', price: ''
  });
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  // Format current time as HH:MM
  const now = new Date();
  const currentTime = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');
  const [time, setTime] = useState(currentTime);

  const [paymentMethod, setPaymentMethod] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [bankName, setBankName] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [receiptNumber, setReceiptNumber] = useState('ייקבע בהפקה...'); // Will be set by DB during download

  // Post-generation popup state
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [generatedFileBlob, setGeneratedFileBlob] = useState(null);
  const [generatedFileName, setGeneratedFileName] = useState('');
  const [generatedReceiptNum, setGeneratedReceiptNum] = useState('');
  const [generatedFileUrl, setGeneratedFileUrl] = useState('');

  const receiptRef = useRef(null);

  useEffect(() => {
    if (sessionStorage.getItem('receipt_auth_pin') === 'rl090692') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleAuth = (e) => {
    e.preventDefault();
    if (pin === 'rl090692') {
      sessionStorage.setItem('receipt_auth_pin', 'rl090692');
      setIsAuthenticated(true);
    } else {
      alert('קוד אינו תקין'); // Invalid PIN in Hebrew
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('receipt_auth_pin');
    setIsAuthenticated(false);
  };

  const handleDownloadAndSave = async () => {
    const element = receiptRef.current;
    if (!element) return;

    // If the receipt has no finalized items but there is a filled draft item, auto-add it so it doesn't fail.
    let itemsToProcess = [...items];
    if (itemsToProcess.length === 0 && draftItem.price && draftItem.price.toString().trim() !== '') {
      itemsToProcess.push(draftItem);
      setItems(itemsToProcess);
      setDraftItem({ id: Date.now(), lessonType: 'שיעור רגיל', quantity: '', price: '' });
      // We must exit and let React re-render the Preview before capturing it
      setTimeout(handleDownloadAndSave, 300);
      return;
    }

    if (itemsToProcess.length === 0) {
      alert('לא הוספו פריטים לקבלה. יש למלא פריט וללחוץ על "אשר פריט"');
      return;
    }

    if (!payerName || !paymentMethod) {
      alert('חובה למלא שם משלם ואמצעי תשלום');
      return;
    }

    setIsSaving(true);

    try {
      // 1. Get and increment sequential receipt number from Firestore using a Transaction
      const counterRef = doc(db, 'config', 'receiptCounter');
      let newReceiptNum = 1;

      await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);
        if (!counterDoc.exists()) {
          transaction.set(counterRef, { lastNumber: 1 });
          newReceiptNum = 1;
        } else {
          newReceiptNum = counterDoc.data().lastNumber + 1;
          transaction.update(counterRef, { lastNumber: newReceiptNum });
        }
      });

      // Update state so the UI reflects the real receipt number
      setReceiptNumber(newReceiptNum);

      // Wait a moment for React to flush the state update to the DOM before capturing canvas
      await new Promise(resolve => setTimeout(resolve, 150));

      // 2. Generate the Image Canvas (now has the correct number)
      const canvas = await html2canvas(element, { 
        scale: 2,
        windowWidth: 800
      });
      const imgData = canvas.toDataURL('image/png');

      let fileBlob = null;
      let fileName = `קבלה-${payerName || 'לקוח'}-${date}.pdf`;

      // 3. Prepare the PDF format
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [canvas.width / 2, canvas.height / 2]
      });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 2, canvas.height / 2);
      fileBlob = pdf.output('blob');

      // 4. Upload to Firebase Storage
      const storageRef = ref(storage, `receipts/${newReceiptNum}-${fileName}`);
      const uploadResult = await uploadBytes(storageRef, fileBlob);
      const downloadUrl = await getDownloadURL(uploadResult.ref);

      // 5. Save to Firestore
      const shareToken = Math.random().toString(36).substring(2, 12); // Generate a 10 char random token
      const currentPriceForDb = itemsToProcess.reduce((total, item) => {
        const unitPriceForDb = parseFloat(item.price) || 0;
        const validQtyForDb = item.quantity ? Number(item.quantity) : 1;
        return total + (unitPriceForDb * validQtyForDb);
      }, 0);

      const [year, month, day] = date.split('-');
      const [hours, minutes] = time.split(':');
      const receiptDateObj = new Date(year, month - 1, day, hours, minutes);

      const lessonTypeSummary = itemsToProcess.map(item => item.lessonType).join(', ');

      await addDoc(collection(db, 'receipts'), {
        clientName: payerName || 'לקוח זמני',
        amount: currentPriceForDb,
        receiptNumber: newReceiptNum,
        date: receiptDateObj,
        dateString: date,
        time: time,
        lessonType: lessonTypeSummary,
        items: itemsToProcess.map(item => ({
          lessonType: item.lessonType,
          quantity: item.quantity ? Number(item.quantity) : 1,
          price: parseFloat(item.price) || 0
        })),
        paymentMethod: paymentMethod,
        referenceNumber: referenceNumber || null,
        bankName: bankName || null,
        fileUrl: downloadUrl,
        shareToken: shareToken,
        createdAt: serverTimestamp()
      });

      // 6. Success! Show the popup instead of downloading immediately
      setGeneratedFileBlob(fileBlob);
      setGeneratedFileName(fileName);
      setGeneratedReceiptNum(newReceiptNum);
      setGeneratedFileUrl(`${window.location.origin}/v/${shareToken}`);
      setShowSuccessPopup(true);

    } catch (err) {
      console.error('Error generating and saving receipt:', err);
      alert('שגיאה חמורה: לא ניתן היה להפיק. ודא חיבור לאינטרנט ונסה שוב.');
    } finally {
      setIsSaving(false);
      // Reset the preview number for the next receipt
      setReceiptNumber('ייקבע בהפקה...');
    }
  };

  const handlePopupDownload = () => {
    if (!generatedFileBlob) return;
    const link = document.createElement('a');
    link.download = generatedFileName;
    link.href = URL.createObjectURL(generatedFileBlob);
    link.click();
  };

  const handlePopupShare = async () => {
    if (!generatedFileUrl) return;

    const lessonTypeSummary = items.map(item => item.lessonType).join(', ');
    const shareText = `שלום ${payerName}, מצורפת קבלה מס' ${generatedReceiptNum} עבור ${lessonTypeSummary}. לצפייה בקבלה: ${generatedFileUrl}`;

    try {
      if (navigator.share) {
        // This shares the LINK, not the file. It's much more stable.
        await navigator.share({
          title: `קבלה מרן לוי`,
          text: shareText,
          // We don't send 'files' here, just the text and URL
        });
      } else {
        // Fallback for Desktop/Older Android: Open WhatsApp directly
        const encodedText = encodeURIComponent(shareText);
        window.open(`https://wa.me/?text=${encodedText}`, '_blank');
      }
    } catch (err) {
      console.log("Share failed or cancelled", err);
    }
  };

  const handleClosePopup = () => {
    setPayerName('');
    setItems([]);
    setDraftItem({ id: Date.now(), lessonType: 'שיעור רגיל', quantity: '', price: '' });
    setPaymentMethod('');
    setReferenceNumber('');
    setBankName('');

    setShowSuccessPopup(false);
    setGeneratedFileBlob(null);
    setGeneratedFileName('');
    setGeneratedReceiptNum('');
    setGeneratedFileUrl('');
  };

  const currentPrice = items.reduce((total, item) => {
    const unitPrice = parseFloat(item.price) || 0;
    const qty = item.quantity ? Number(item.quantity) : 1;
    return total + (unitPrice * qty);
  }, 0);
  const priceWithoutVat = (currentPrice / (1 + VAT_RATE)).toFixed(2);
  const vatAmount = (currentPrice - priceWithoutVat).toFixed(2);

  const updateDraftItem = (field, value) => {
    setDraftItem(prev => ({ ...prev, [field]: value }));
  };

  const confirmDraftItem = () => {
    if (!draftItem.price) {
      alert('יש להזין מחיר לפריט לפני הוספתו לקבלה');
      return;
    }
    setItems(prev => [...prev, draftItem]);
    setDraftItem({ id: Date.now() + Math.random(), lessonType: 'שיעור רגיל', quantity: '', price: '' });
  };

  const removeItem = (id) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const editItem = (item) => {
    if (draftItem.price && draftItem.price.toString().trim() !== '') {
      const confirmOverride = window.confirm('האם ברצונך למחוק את הפריט שאתה עורך כרגע ולערוך במקומו את הפריט הקיים?');
      if (!confirmOverride) return;
    }
    setDraftItem(item);
    setItems(prev => prev.filter(i => i.id !== item.id));
  };

  if (!isAuthenticated) {
    return (
      <div className="receipt-auth-container">
        <form onSubmit={handleAuth} className="receipt-auth-form">
          <h2>כניסה למערכת קבלות</h2>
          <p>הזן קוד גישה כדי להמשיך</p>
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="קוד גישה"
            required
          />
          <button type="submit" className="primary-btn">כניסה</button>
        </form>
      </div>
    );
  }

  return (
    <div className="receipt-page-container">
      <div className="receipt-controls">
        <h2>הפקת קבלה (חשבונית מס)</h2>
        <div className="control-group">
          <label>שם המשלם:</label>
          <input
            type="text"
            value={payerName}
            onChange={(e) => setPayerName(e.target.value)}
            placeholder="לכבוד..."
            required
          />
        </div>
        <div className="control-group">
          <label>תאריך:</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="control-group">
          <label>שעה:</label>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
        </div>
        <div className="items-section" style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px', marginBottom: '20px', backgroundColor: '#f8fafc' }}>
          <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '1.1rem', color: '#2d3748' }}>פריטים בחשבונית</h3>
          {/* Compact Chips for Confirmed Items */}
          {items.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '15px' }}>
              {items.map(item => (
                <div key={item.id} style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#edf2f7', padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', color: '#2d3748', border: '1px solid #cbd5e0' }}>
                  <span style={{ fontWeight: '600' }}>{item.lessonType}</span>
                  {Number(item.quantity) > 1 && <span style={{ marginRight: '4px' }}>(x{item.quantity})</span>}
                  <span style={{ margin: '0 6px', color: '#a0aec0' }}>|</span>
                  <span>₪{((parseFloat(item.price) || 0) * (item.quantity ? Number(item.quantity) : 1)).toFixed(2)}</span>
                  <button type="button" onClick={() => editItem(item)} style={{ background: 'none', border: 'none', color: '#3182ce', cursor: 'pointer', fontSize: '0.9rem', marginRight: '8px', padding: 0, fontWeight: 'bold' }} title="ערוך פריט">
                    ערוך
                  </button>
                  <button type="button" onClick={() => removeItem(item.id)} style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: '1.2rem', marginRight: '8px', padding: 0, lineHeight: 1, display: 'flex', alignItems: 'center', fontWeight: 'bold' }} title="הסר פריט">
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Active form for drafting a new item */}
          <div className="item-group" style={{ paddingTop: items.length > 0 ? '15px' : '0', borderTop: items.length > 0 ? '1px dashed #cbd5e0' : 'none' }}>
            <div className="control-group">
              <label>תיאור שירות:</label>
              <select value={draftItem.lessonType} onChange={(e) => updateDraftItem('lessonType', e.target.value)}>
                {LESSON_TYPES.map((type, idx) => (
                  <option key={idx} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="control-group">
              <label>כמות:</label>
              <input
                type="number"
                min="1"
                value={draftItem.quantity}
                onChange={(e) => updateDraftItem('quantity', e.target.value)}
                placeholder="1 (ברירת מחדל)"
              />
            </div>
            <div className="control-group">
              <label>מחיר ליחידה לחשבונית (כולל מע"מ):</label>
              <input
                type="number"
                value={draftItem.price}
                onChange={(e) => updateDraftItem('price', e.target.value)}
                placeholder="הכנס סכום ב-₪"
              />
            </div>
          </div>
          <button type="button" onClick={confirmDraftItem} className="secondary-btn" style={{ marginTop: '15px', width: '100%', border: '1px solid #4a5568', backgroundColor: '#e2e8f0', color: '#2d3748', fontWeight: 'bold' }}>+ אשר והוסף לקבלה</button>
        </div>

        <div className="control-group">
          <label>אמצעי תשלום (לדוגמה: ביט, פייבוקס, מזומן):</label>
          <input
            type="text"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            required
            placeholder="חובה למלא"
          />
        </div>

        <div className="control-group">
          <label>מספר אסמכתא (אופציונלי):</label>
          <input
            type="text"
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
          />
        </div>

        <div className="control-group">
          <label>שם בנק (אופציונלי):</label>
          <input
            type="text"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
          />
        </div>
        <div className="receipt-actions">
          <button onClick={() => window.location.href = '/receipt-history'} className="secondary-btn" disabled={isSaving} style={{ backgroundColor: '#e2e8f0', color: '#4a5568' }}>היסטוריית קבלות</button>
          <button onClick={handleDownloadAndSave} className="primary-btn" disabled={isSaving}>
            {isSaving ? 'מפיק, שומר ומוריד PDF...' : 'הפק והורד PDF'}
          </button>
          <button onClick={handleLogout} className="secondary-btn" style={{ backgroundColor: '#f56565', color: 'white', marginTop: '10px' }} disabled={isSaving}>התנתק (Sign Out)</button>
        </div>
      </div>

      {showSuccessPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <div className="popup-icon" style={{ fontSize: '48px', marginBottom: '10px' }}>✅</div>
            <h3>קבלה {generatedReceiptNum} נשמרה בהצלחה!</h3>
            <p>מה תרצה לעשות כעת?</p>
            <div className="popup-actions" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px' }}>
              <button className="primary-btn" onClick={handlePopupDownload} style={{ width: '100%', margin: 0 }}>הורד PDF ברגע זה</button>
              <button className="primary-btn" onClick={handlePopupShare} style={{ width: '100%', margin: 0, backgroundColor: '#25D366' }}>שתף קבלה (WhatsApp)</button>
              <button className="secondary-btn" onClick={handleClosePopup} style={{ width: '100%', margin: 0 }}>סגור והתחל קבלה חדשה</button>
            </div>
          </div>
        </div>
      )}

      <div className="receipt-preview-wrapper" dir="rtl">
        <div className="receipt-element" ref={receiptRef}>
          <div className="receipt-header">
            <div className="receipt-header-right" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <img src="/reciept.jpeg" alt="לוגו" style={{ width: '80px', height: '100px', objectFit: 'cover', borderRadius: '8px' }} crossOrigin="anonymous" />
              <div className="receipt-header-text">
                <h1>רן לוי - מורה לנהיגה</h1>
                <p>ע.מ.: 204457105</p> {/* Placeholder, should be the real VAT ID if they want it */}
                <p>טלפון: 050-3810050</p>
              </div>
            </div>
            <div className="receipt-title-box">
              <h2>חשבונית מס / קבלה</h2>
              <p>מספר: {receiptNumber}</p>
              <p>תאריך: {date.split('-').reverse().join('/')}</p>
              <p>שעה: {time}</p>
            </div>
          </div>

          <div className="receipt-customer-info">
            <p><strong>לכבוד:</strong> {payerName || '_______________'}</p>
          </div>

          <table className="receipt-table">
            <thead>
              <tr>
                <th>תיאור</th>
                <th>כמות</th>
                <th>סה"כ</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan="3" style={{ textAlign: 'center', color: '#a0aec0' }}>טרם אושרו פריטים</td>
                </tr>
              )}
              {items.map((item, index) => {
                const itemUnitPrice = parseFloat(item.price) || 0;
                const itemQty = item.quantity ? Number(item.quantity) : 1;
                const itemTotal = itemUnitPrice * itemQty;
                return (
                  <tr key={item.id || index}>
                    <td>{item.lessonType}</td>
                    <td>{itemQty}</td>
                    <td>₪{itemTotal.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="receipt-totals">
            <div className="totals-row">
              <span>סה"כ לפני מע"מ:</span>
              <span>₪{priceWithoutVat}</span>
            </div>
            <div className="totals-row">
              <span>מע"מ ({VAT_RATE * 100}%):</span>
              <span>₪{vatAmount}</span>
            </div>
            <div className="totals-row total-final">
              <span>סה"כ לתשלום:</span>
              <span>₪{currentPrice}</span>
            </div>
          </div>

          <div className="receipt-payment-info" style={{ marginTop: '20px', borderTop: '1px solid #ccc', paddingTop: '10px' }}>
            <p><strong>אמצעי תשלום:</strong> {paymentMethod || '______________'}</p>
            {referenceNumber && <p><strong>מספר אסמכתא:</strong> {referenceNumber}</p>}
            {bankName && <p><strong>שם בנק:</strong> {bankName}</p>}
          </div>
          <div className="receipt-footer">
            <p>המסמך הופק דיגיטלית.</p>
            <p>תודה רבה ונסיעה בטוחה!</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptPage;
